import { api, ApiError } from './client';

export type FilePurpose =
  | 'EKYC_ID_FRONT'
  | 'EKYC_ID_BACK'
  | 'EKYC_SELFIE'
  | 'FARM_LOG_ATTACHMENT'
  | 'FIELD_PHOTO'
  | 'MATERIAL_ATTACHMENT'
  | 'CONTRACT_DOCUMENT'
  | 'MANUAL_CONFIRMATION_ATTACHMENT'
  | 'FARMER_IDENTITY_DOCUMENT'
  | 'LAND_TITLE_DOCUMENT'
  | 'OTHER';

interface PresignUploadResponse {
  fileId: string;
  uploadUrl: string;
  expiresAt: string;
}

/**
 * A MediaRecorder blob's `type` carries codec parameters — `video/webm;codecs=vp9,opus`,
 * with an *unquoted comma* inside the parameter. That value is legal per the spec
 * but trips media-type parsers all along the path (Express's own `type-is` is
 * documented to mis-parse it in main.ts, and edge proxies reject the header
 * outright), which is why photos — `image/jpeg`, no parameters — upload fine while
 * video does not. The parameters tell the server nothing it uses, so both the
 * presign and the raw PUT travel with the base type only. It also makes the
 * stored file's extension resolvable: `video/webm` maps to `.webm`,
 * `video/webm;codecs=…` mapped to nothing.
 */
export function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0].trim().toLowerCase();
}

/** Presigns then uploads a captured photo/video/audio Blob, returning the fileId
 * to attach to a FarmLog (or any other `{fileId, type, caption?}` attachment). */
export async function uploadFile(blob: Blob, purpose: FilePurpose, mimeType: string): Promise<string> {
  const type = baseMimeType(mimeType) || 'application/octet-stream';
  const presign = await api.post<PresignUploadResponse>('/files/presign', { purpose, mimeType: type });
  await api.uploadRaw(presign.uploadUrl, blob, type);
  return presign.fileId;
}

/**
 * Upload errors used to collapse into "Tải lên thất bại, vui lòng thử lại",
 * which is indistinguishable from nothing having happened — a tester watching a
 * video upload fail reported seeing no message at all. Say what the server said,
 * and name the two failures a farmer can actually act on.
 */
export function uploadFailureMessage(err: unknown, what: string): string {
  if (!(err instanceof ApiError)) {
    return `Tải lên ${what} thất bại — kiểm tra kết nối mạng rồi thử lại.`;
  }
  if (err.status === 413) {
    return `${what} quá lớn để tải lên. Hãy quay một đoạn ngắn hơn rồi thử lại.`;
  }
  const detail =
    typeof err.body === 'object' && err.body !== null && 'message' in err.body
      ? String((err.body as { message?: unknown }).message)
      : '';
  return `Tải lên ${what} thất bại (lỗi ${err.status})${detail ? `: ${detail}` : ''}`;
}

interface PresignDownloadResponse {
  downloadUrl: string;
  expiresAt: string;
}

/** Resolves a stored fileId to a playable/displayable URL. Call this whenever
 * rendering a `{fileId, type, caption?}` attachment — the URL isn't stored
 * alongside the fileId since the backend may rotate/regenerate it. */
export async function getFileDownloadUrl(fileId: string): Promise<string> {
  const res = await api.get<PresignDownloadResponse>(`/files/${fileId}/presign-download`);
  return res.downloadUrl;
}
